import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  Badge,
  Table,
  IconButton,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Alert,
} from '@chakra-ui/react'
import { Plus, Trash2, AlertCircle, Check, Info } from 'lucide-react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import { useCreateTransaction, useUpdateTransactionWithLines, useJournals, useResidenceChartAccounts } from '../hooks'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { ResidenceChartAccount, Transaction } from '../types'

// Mode de la modal
export type TransactionModalMode = 'create' | 'edit' | 'duplicate'

interface TransactionLine {
  id: string
  residence_chart_account_id: string
  label: string
  debit: number
  credit: number
}

interface TransactionCreateModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
  fiscalYearId?: string
  modalMode?: TransactionModalMode
  transactionToEdit?: Transaction | null
}

export function TransactionCreateModal({
  isOpen,
  onClose,
  residenceId,
  fiscalYearId,
  modalMode = 'create',
  transactionToEdit,
}: TransactionCreateModalProps) {
  const { data: journals } = useJournals()
  const { data: residenceAccounts } = useResidenceChartAccounts(residenceId)
  const createTransaction = useCreateTransaction()
  const updateTransaction = useUpdateTransactionWithLines()

  // Filtrer uniquement les comptes actifs
  const accounts = residenceAccounts?.filter((account) => account.is_active)

  const isEditMode = modalMode === 'edit'
  const isDuplicateMode = modalMode === 'duplicate'

  // Données du formulaire
  const [formData, setFormData] = useState({
    journal_id: '',
    transaction_date: new Date().toISOString().split('T')[0],
    description: '',
    reference: '',
  })

  const [lines, setLines] = useState<TransactionLine[]>([
    { id: crypto.randomUUID(), residence_chart_account_id: '', label: '', debit: 0, credit: 0 },
    { id: crypto.randomUUID(), residence_chart_account_id: '', label: '', debit: 0, credit: 0 },
  ])

  // Calculer les totaux
  const totals = useMemo(() => {
    return lines.reduce(
      (acc, line) => ({
        debit: acc.debit + (line.debit || 0),
        credit: acc.credit + (line.credit || 0),
      }),
      { debit: 0, credit: 0 }
    )
  }, [lines])

  const isBalanced = Math.abs(totals.debit - totals.credit) < 0.01
  const hasValidLines = lines.some((l) => l.residence_chart_account_id && (l.debit > 0 || l.credit > 0))

  // Reset du formulaire
  const resetForm = () => {
    setFormData({
      journal_id: '',
      transaction_date: new Date().toISOString().split('T')[0],
      description: '',
      reference: '',
    })
    setLines([
      { id: crypto.randomUUID(), residence_chart_account_id: '', label: '', debit: 0, credit: 0 },
      { id: crypto.randomUUID(), residence_chart_account_id: '', label: '', debit: 0, credit: 0 },
    ])
  }

  // Initialiser le formulaire en mode édition/duplication
  useEffect(() => {
    if (isOpen && transactionToEdit && (isEditMode || isDuplicateMode)) {
      // Remplir les données du formulaire
      setFormData({
        journal_id: transactionToEdit.journal_id || '',
        transaction_date: transactionToEdit.transaction_date || new Date().toISOString().split('T')[0],
        description: transactionToEdit.description || '',
        // En mode duplication, vider la référence car elle doit être unique
        reference: isDuplicateMode ? '' : (transactionToEdit.reference || ''),
      })

      // Remplir les lignes
      if (transactionToEdit.lines && transactionToEdit.lines.length > 0) {
        setLines(
          transactionToEdit.lines.map((line) => ({
            id: crypto.randomUUID(),
            residence_chart_account_id: line.residence_chart_account_id || '',
            label: line.label || '',
            debit: line.debit || 0,
            credit: line.credit || 0,
          }))
        )
      }
    }
  }, [isOpen, transactionToEdit, isEditMode, isDuplicateMode])

  // Grouper les comptes par classe pour l'affichage
  const groupedAccounts = useMemo(() => {
    if (!accounts) return {}
    const groups: Record<string, ResidenceChartAccount[]> = {}
    accounts.forEach((account) => {
      if (!account.chart_account) return
      const classNum = account.chart_account.code.charAt(0)
      if (!groups[classNum]) groups[classNum] = []
      groups[classNum].push(account)
    })
    // Trier les comptes dans chaque groupe par code (tri hiérarchique comptable)
    Object.keys(groups).forEach((classNum) => {
      groups[classNum]?.sort((a, b) => {
        const codeA = a.custom_code || a.chart_account?.code || ''
        const codeB = b.custom_code || b.chart_account?.code || ''

        // Tri comptable hiérarchique :
        // 51 > 511 > 51101 > 51102 > 512 > 51201 > 51202 > 5121 > 513
        // Comparaison caractère par caractère, le code le plus court vient avant s'il est préfixe
        const minLength = Math.min(codeA.length, codeB.length)

        for (let i = 0; i < minLength; i++) {
          const charA = codeA.charAt(i)
          const charB = codeB.charAt(i)
          if (charA !== charB) {
            return charA.localeCompare(charB)
          }
        }

        // Si un code est préfixe de l'autre, le plus court vient avant
        // Exemple: 51 avant 511, 512 avant 51201
        return codeA.length - codeB.length
      })
    })
    return groups
  }, [accounts])

  const getClassName = (classNum: string) => {
    const names: Record<string, string> = {
      '1': 'Capitaux',
      '4': 'Tiers',
      '5': 'Financiers',
      '6': 'Charges',
      '7': 'Produits',
    }
    return names[classNum] || `Classe ${classNum}`
  }

  const handleAddLine = () => {
    setLines((prev) => [
      ...prev,
      { id: crypto.randomUUID(), residence_chart_account_id: '', label: '', debit: 0, credit: 0 },
    ])
  }

  const handleRemoveLine = (id: string) => {
    if (lines.length <= 2) {
      toaster.create({
        title: 'Minimum 2 lignes',
        description: 'Une écriture doit avoir au minimum 2 lignes',
        type: 'warning',
      })
      return
    }
    setLines((prev) => prev.filter((l) => l.id !== id))
  }

  const handleLineChange = (
    id: string,
    field: keyof TransactionLine,
    value: string | number
  ) => {
    setLines((prev) =>
      prev.map((l) =>
        l.id === id
          ? { ...l, [field]: field === 'debit' || field === 'credit' ? Number(value) || 0 : value }
          : l
      )
    )
  }

  // Sélection du compte (sans auto-complétion du label)
  const handleAccountChange = (lineId: string, residenceAccountId: string) => {
    setLines((prev) =>
      prev.map((l) =>
        l.id === lineId
          ? { ...l, residence_chart_account_id: residenceAccountId }
          : l
      )
    )
  }

  const handleSubmit = async () => {
    if (!formData.journal_id || !formData.description || !formData.transaction_date) {
      toaster.create({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs obligatoires',
        type: 'error',
      })
      return
    }

    if (!hasValidLines) {
      toaster.create({
        title: 'Erreur',
        description: 'Veuillez saisir au moins une ligne avec un compte et un montant',
        type: 'error',
      })
      return
    }

    if (!isBalanced) {
      toaster.create({
        title: 'Erreur',
        description: 'L\'écriture n\'est pas équilibrée (total débit ≠ total crédit)',
        type: 'error',
      })
      return
    }

    try {
      const validLines = lines
        .filter((l) => l.residence_chart_account_id && (l.debit > 0 || l.credit > 0))
        .map((l) => ({
          residence_chart_account_id: l.residence_chart_account_id,
          debit: l.debit || 0,
          credit: l.credit || 0,
          label: l.label || undefined,
        }))

      if (isEditMode && transactionToEdit) {
        // Mode édition : mise à jour
        await updateTransaction.mutateAsync({
          id: transactionToEdit.id,
          transaction: {
            journal_id: formData.journal_id,
            transaction_date: formData.transaction_date,
            description: formData.description,
            reference: formData.reference || undefined,
          },
          lines: validLines,
        })

        toaster.create({
          title: 'Succès',
          description: 'Écriture modifiée avec succès',
          type: 'success',
        })
      } else {
        // Mode création ou duplication : nouvelle écriture
        await createTransaction.mutateAsync({
          journal_id: formData.journal_id,
          residence_id: residenceId,
          fiscal_year_id: fiscalYearId || '',
          transaction_date: formData.transaction_date,
          description: formData.description,
          reference: formData.reference || undefined,
          lines: validLines,
        })

        toaster.create({
          title: 'Succès',
          description: isDuplicateMode ? 'Écriture dupliquée avec succès' : 'Écriture créée avec succès',
          type: 'success',
        })
      }

      handleClose()
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de l\'enregistrement',
        type: 'error',
      })
    }
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => !e.open && handleClose()}
      size="xl"
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="900px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              {isEditMode
                ? 'Modifier l\'écriture comptable'
                : isDuplicateMode
                ? 'Dupliquer l\'écriture comptable'
                : 'Nouvelle écriture comptable'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {/* Alerte pour le mode duplication */}
            {isDuplicateMode && (
              <Alert.Root status="warning" mb={4}>
                <Alert.Indicator>
                  <Info size={16} />
                </Alert.Indicator>
                <Alert.Content>
                  <Alert.Description>
                    <Text fontWeight="medium">Duplication d'une écriture</Text>
                    <Text fontSize="sm">
                      La référence doit être saisie car elle ne peut pas être identique
                      à l'écriture d'origine ({transactionToEdit?.reference || 'N/A'}).
                    </Text>
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            {/* Formulaire de saisie */}
            <Box>
              {/* En-tête du formulaire */}
              <Flex direction="column" gap={4} mb={4}>
                <Flex gap={4}>
                  <Box flex={1}>
                    <Field label="Date" required>
                      <Input
                        type="date"
                        value={formData.transaction_date}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, transaction_date: e.target.value }))
                        }
                      />
                    </Field>
                  </Box>

                  <Box flex={1}>
                    <Field label="Journal" required>
                    <NativeSelectRoot>
                      <NativeSelectField
                        value={formData.journal_id}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, journal_id: e.target.value }))
                        }
                      >
                        <option value="">Sélectionner...</option>
                        {journals?.map((j) => (
                          <option key={j.id} value={j.id}>
                            {j.code} - {j.name}
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                    </Field>
                  </Box>
                </Flex>

                <Field label="Libellé de l'opération" required>
                  <Input
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Ex: Facture EDF novembre 2024"
                  />
                </Field>

                <Field label="Référence">
                  <Input
                    value={formData.reference}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, reference: e.target.value }))
                    }
                    placeholder="Ex: FA-2024-001"
                  />
                </Field>
              </Flex>

              {/* Tableau des lignes */}
              <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row bg="gray.50">
                      <Table.ColumnHeader w="35%">Compte</Table.ColumnHeader>
                      <Table.ColumnHeader>Libellé ligne</Table.ColumnHeader>
                      <Table.ColumnHeader w="100px" textAlign="right">
                        Débit
                      </Table.ColumnHeader>
                      <Table.ColumnHeader w="100px" textAlign="right">
                        Crédit
                      </Table.ColumnHeader>
                      <Table.ColumnHeader w="40px"></Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {lines.map((line) => (
                      <Table.Row key={line.id}>
                        <Table.Cell>
                          <NativeSelectRoot size="sm">
                            <NativeSelectField
                              value={line.residence_chart_account_id}
                              onChange={(e) => handleAccountChange(line.id, e.target.value)}
                            >
                              <option value="">Compte...</option>
                              {Object.entries(groupedAccounts).map(([classNum, classAccounts]) => (
                                <optgroup key={classNum} label={getClassName(classNum)}>
                                  {classAccounts.map((account) => (
                                    <option key={account.id} value={account.id}>
                                      {account.custom_code || account.chart_account?.code} - {account.custom_name || account.chart_account?.name}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </NativeSelectField>
                          </NativeSelectRoot>
                        </Table.Cell>
                        <Table.Cell>
                          <Input
                            size="sm"
                            value={line.label}
                            onChange={(e) => handleLineChange(line.id, 'label', e.target.value)}
                            placeholder="Libellé..."
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Input
                            size="sm"
                            type="number"
                            step="0.01"
                            min="0"
                            textAlign="right"
                            value={line.debit || ''}
                            onChange={(e) => handleLineChange(line.id, 'debit', e.target.value)}
                            placeholder="0,00"
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Input
                            size="sm"
                            type="number"
                            step="0.01"
                            min="0"
                            textAlign="right"
                            value={line.credit || ''}
                            onChange={(e) => handleLineChange(line.id, 'credit', e.target.value)}
                            placeholder="0,00"
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <IconButton
                            variant="ghost"
                            size="xs"
                            colorPalette="red"
                            aria-label="Supprimer"
                            onClick={() => handleRemoveLine(line.id)}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Table.Cell>
                      </Table.Row>
                    ))}

                    {/* Ligne d'ajout */}
                    <Table.Row>
                      <Table.Cell colSpan={5}>
                        <Button
                          variant="ghost"
                          size="xs"
                          colorPalette="brand"
                          onClick={handleAddLine}
                        >
                          <Plus size={14} />
                          Ajouter une ligne
                        </Button>
                      </Table.Cell>
                    </Table.Row>

                    {/* Ligne de totaux */}
                    <Table.Row bg="gray.100" fontWeight="bold">
                      <Table.Cell colSpan={2} textAlign="right">
                        TOTAUX
                      </Table.Cell>
                      <Table.Cell textAlign="right" fontFamily="mono">
                        {formatCurrency(totals.debit)}
                      </Table.Cell>
                      <Table.Cell textAlign="right" fontFamily="mono">
                        {formatCurrency(totals.credit)}
                      </Table.Cell>
                      <Table.Cell></Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table.Root>
              </Box>

              {/* Indicateur d'équilibre */}
              <Flex mt={3} justify="flex-end">
                {isBalanced ? (
                  <Badge colorPalette="green" size="lg">
                    <Check size={14} />
                    Écriture équilibrée
                  </Badge>
                ) : (
                  <Badge colorPalette="red" size="lg">
                    <AlertCircle size={14} />
                    Écart : {formatCurrency(Math.abs(totals.debit - totals.credit))}
                  </Badge>
                )}
              </Flex>
            </Box>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={createTransaction.isPending || updateTransaction.isPending}
              disabled={!isBalanced || !hasValidLines}
            >
              {isEditMode
                ? 'Enregistrer les modifications'
                : isDuplicateMode
                ? 'Créer la copie'
                : 'Créer l\'écriture'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
